import { useState, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@lms/ui';
import { X } from 'lucide-react';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';

interface ProfileDialogProps {
  title: string;
  description: string;
  busy?: boolean;
  onClose: () => void;
  children: ReactNode;
}

/** Localized, pending-safe shell using the shared Radix focus trap. */
export function ProfileDialog({
  title,
  description,
  busy = false,
  onClose,
  children,
}: ProfileDialogProps) {
  const { t, language } = useLanguage();
  const [returnFocus] = useState(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null
  );
  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        className="flex max-h-[92dvh] w-[calc(100%-2rem)] max-w-2xl flex-col gap-0 overflow-hidden border-0 p-0 sm:p-0 [&>button:last-child]:hidden"
        onEscapeKeyDown={event => {
          if (busy) event.preventDefault();
        }}
        onInteractOutside={event => {
          if (busy) event.preventDefault();
        }}
        onCloseAutoFocus={event => {
          event.preventDefault();
          returnFocus?.focus();
        }}
      >
        <header className="shrink-0 space-y-2 px-6 pb-4 pt-6 pe-16">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </header>
        <button
          type="button"
          aria-label={t(copy.common.close)}
          disabled={busy}
          onClick={onClose}
          className="absolute end-4 top-4 rounded-lg p-2 text-on-surface-variant hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-40 active:scale-95 duration-150"
        >
          <X size={20} aria-hidden="true" />
        </button>
        {children}
      </DialogContent>
    </Dialog>
  );
}
